import { TeamsIcon, GitlabIcon, SharepointIcon, RedmineIcon, JiraIcon } from 'framework/ui/mui-builder';
import { EXTERNAL_RESOURCE_TYPE } from '../constants';
import { CustomerReportStatus, RecipientType, ApprovalType } from '../customerReport/types';
import LinkIcon from '@mui/icons-material/Link';
import { SvgIconProps } from '@mui/material';
import { FigmaIcon } from 'framework/ui/mui-builder';
import { RisksType } from '../risks/types';
import { BillingType } from '../billingTypes';
import { StaffingRisk } from 'domain/shared/staffing';
import { AccessInfo } from '../accessInfoTypes';

enum ReportFrequency {
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
}

export type HealthZone = 'OTHER' | 'SAFE' | 'MONITORING' | 'CRITICAL' | 'ESCALATION';

export enum HealthcheckStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum MilestoneType {
  ADVANCED_PAYMENT = 'ADVANCED_PAYMENT',
  PROGRESS_BILLING = 'PROGRESS_BILLING',
  ACCEPTANCE_MILESTONE = 'ACCEPTANCE_MILESTONE',
  NON_BILLING_MILESTONE = 'NON_BILLING_MILESTONE',
  NONE = 'NONE',
}

export enum MilestoneStatus {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
}

enum CycleFrequency {
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
}

export enum CycleSettingsStatus {
  IN_APPROVAL = 'IN_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

enum WeekDay {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

enum CycleActivityStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  SKIPPED = 'SKIPPED',
  EXPIRED = 'EXPIRED',
}

interface ProjectAzureGroup {
  azureId: string;
  key: string;
  name: string;
}

interface ProjectOnePasswordVault {
  vaultId: string;
  key: string;
  name: string;
}

export interface ExternalResource {
  name: string;
  value: string;
}

interface ProjectAccountData {
  sowId?: number;
  accountId?: string;
  accountName?: string;
  isAccountCodeName?: boolean;
  ownerAlias?: string;
}

interface ProjectBudgetData {
  defaultBudgetId?: number;
  availableBudgets: number[];
  assignmentForbidden?: boolean;
}

interface ProjectAuditData {
  lastReportDate?: string;
  lastUpdate?: string;
  archivedAt?: string;
  activatedAt?: string;
}

interface ProjectSensitiveData {
  originalName?: string;
  onePasswordVaults?: ProjectOnePasswordVault[];
  azureGroups?: ProjectAzureGroup[];
}

interface ProjectResourcesData {
  externalResources: ExternalResource[];
  communicationResourceEnabled?: boolean;
  vcsResourceEnabled?: boolean;
  taskManagementResourceEnabled?: boolean;
  passwordManagementResourceEnabled?: boolean;
}

interface ReportSettings {
  reportFrequency: ReportFrequency;
  isSystemReportUsed: boolean;
  approvalType: ApprovalType;
}

interface CycleActivitySettings {
  cycleFrequency?: CycleFrequency;
  cycleStartDay?: WeekDay;
  cycleEndDay?: WeekDay;
  planningDay?: WeekDay;
  demoDay?: WeekDay;
  cycleSettingsStatus?: CycleSettingsStatus;
  cycleChangeReason?: string;
  cycleRejectionReason?: string;
}

interface Settings {
  id?: string;
  projectId: number;
  reportSettings?: ReportSettings;
  cycleActivitySettings?: CycleActivitySettings;
}

export interface BudgetStructure {
  id?: string;
  hours?: string;
  source?: string;
  status?: string;
}

export interface Budget {
  id?: string;
  projectName?: string;
  billingType?: BillingType;
  fte?: string;
  startDate?: string;
  endDate?: string;
  extendedEndDate?: string;
  structure?: BudgetStructure[];
}

export interface ProjectTeamMemberLite {
  employeeAlias: string;
  role: string;
  fte?: number;
  isBench?: boolean;
  isBackup?: boolean;
}

export interface ProjectTeamLite {
  id?: string;
  members?: ProjectTeamMemberLite[];
}

interface Planning {
  id?: string;
  cycleActivityId: string;
  planningDate?: string;
  details?: string;
  plannedScopeLink?: string;
  isSkipped?: boolean;
  skipReason?: string;
}

interface Demo {
  id?: string;
  cycleActivityId: string;
  demoDate?: string;
  details?: string;
  demoRecordLink?: string;
  isSkipped?: boolean;
  skipReason?: string;
}

interface CycleActivity {
  id?: string;
  projectId: number;
  title?: string;
  startDate?: string;
  endDate?: string;
  status?: CycleActivityStatus;
  planning?: Planning;
  demo?: Demo;
}

interface CustomerReport {
  id?: string;
  projectId: number;
  title?: string;
  periodStart?: string;
  periodEnd?: string;
  status?: CustomerReportStatus;
  template?: string;
  externalReportLink?: string;
  rejectionReason?: string;
  pdfReportPath?: string;
  lastUpdate?: string;
}

interface Recipient {
  id?: string;
  projectId: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  type?: RecipientType;
}

// ICONS
export interface ProjectLinkConfig {
  name: string;
  displayName: string;
  Icon: ReactComponent<SvgIconProps>;
  isMultiple: boolean;
  priority?: number;
}

export interface ExternalResourceWithConfig extends ExternalResource {
  config: ProjectLinkConfig;
}

const DEFAULT_PROJECT_LINK_CONFIG: ProjectLinkConfig = {
  name: '',
  displayName: '',
  Icon: LinkIcon,
  isMultiple: true,
};

export const PROJECT_LINK_CONFIG: ProjectLinkConfig[] = [
  {
    name: EXTERNAL_RESOURCE_TYPE.teamsWebUrl,
    displayName: 'Teams',
    priority: 1,
    isMultiple: false,
    Icon: TeamsIcon,
  },
  {
    name: EXTERNAL_RESOURCE_TYPE.redmineWebUrl,
    displayName: 'Redmine',
    priority: 2,
    isMultiple: false,
    Icon: RedmineIcon,
  },
  {
    name: EXTERNAL_RESOURCE_TYPE.gitlabWebUrl,
    displayName: 'Gitlab',
    priority: 3,
    isMultiple: false,
    Icon: GitlabIcon,
  },
  {
    name: EXTERNAL_RESOURCE_TYPE.sharepointWebUrl,
    displayName: 'SharePoint',
    priority: 4,
    isMultiple: false,
    Icon: SharepointIcon,
  },
  {
    name: EXTERNAL_RESOURCE_TYPE.jiraWebUrl,
    displayName: 'Jira',
    priority: 5,
    isMultiple: true,
    Icon: JiraIcon,
  },
  {
    name: EXTERNAL_RESOURCE_TYPE.figmaWebUrl,
    displayName: 'Figma',
    priority: 6,
    isMultiple: true,
    Icon: FigmaIcon,
  },
];

export const getProjectLinkConfigByName = (name: string) => {
  return PROJECT_LINK_CONFIG.find(v => v.name === name) ?? DEFAULT_PROJECT_LINK_CONFIG;
};

export interface Project {
  id: string;
  legacyId?: number;
  name?: string;
  alias?: string;
  projectManager?: string;
  projectDimension?: {
    engagementType?: string;
    commercialState?: string;
  };
  description?: string;
  projectState?: string;
  isArchived?: boolean;
  accountData?: ProjectAccountData;
  budgetData?: ProjectBudgetData;
  auditData?: ProjectAuditData;
  sensitiveData?: ProjectSensitiveData;
  resourcesData?: ProjectResourcesData;
  settings?: Settings;
  milestones?: Milestone[];
  businessOutcomes?: BusinessOutcome[];
  risks?: RisksType[];
  health?: Health;
  engineeringPolicy?: {
    id?: string;
    productionOwnership?: string;
  };
  staffingRisk?: StaffingRisk;
  evm?: Evm;
  cycleActivities?: CycleActivity[];
  customerReports?: CustomerReport[];
  reportRecipients?: Recipient[];
  budget?: Budget;
  team?: ProjectTeamLite;
  externalResources?: ExternalResourceWithConfig[];
  accessInfo: AccessInfo;
}

export interface Milestone {
  id?: string;
  alias?: string;
  title?: string;
  description?: string;
  type?: MilestoneType;
  status?: MilestoneStatus;
  initialPlannedDate?: string;
  currentPlannedDate?: string;
  completionDate?: string;
  rejectionDate?: string;
  comment?: string;
  rejectionReason?: string;
}

export interface BusinessOutcome {
  id?: string;
  alias?: string;
  name?: string;
  status?: string;
  milestones: string[];
  projectId?: number;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  deletedAt?: string;
  deletedBy?: string;
}

interface Health {
  id?: string;
  projectId: number;
  zone?: HealthZone;
  healthChecks: Healthcheck[];
  createdAt?: string;
  updatedAt?: string;
}

export interface Healthcheck {
  id?: string;
  healthId?: string;
  name?: string;
  message?: string;
  targetZone?: HealthZone;
  status?: HealthcheckStatus;
  zoneSince?: string;
  createdAt?: string;
  latestEntityModificationDate?: string;
}

interface Evm {
  id?: string;
  projectId: number;
  projectInfo?: ProjectInfo;
  projectBudget?: ProjectBudget;
  projectTimeline?: ProjectTimeline;
  lastUpdate?: string;
  charts?: EvmChart[];
}

interface ProjectInfo {
  statusUpdate?: string;
  scopeCompletionPercentage?: number;
  kickoffMeetingDate?: string;
  originalReleaseDate?: string;
  projectedReleaseDate?: string;
}

interface ProjectBudget {
  projectedBudgetHours?: number;
  budgetTotalHours?: number;
  budgetUsedHours?: number;
  budgetUsedPercentage?: number;
  budgetRemainingHours?: number;
  cpiBudget?: number;
  cpiBudgetProjectionHours?: number;
}

interface ProjectTimeline {
  timelineTotalBdays?: number;
  timelineUsedBdays?: number;
  timelineUsedPercentage?: number;
  timelineRemainingBdays?: number;
  cpiTimeline?: number;
  cpiTimelineProjectionBdays?: number;
}

interface EvmChart {
  statusUpdate?: string;
  scopeCompletionPercentage?: number;
  cpiBudget?: number;
  budgetUsedHours?: number;
  cpiTimeline?: number;
}

export interface DashboardProject extends Project {
  projectManagerName?: string;
  accountManagerName?: string;
  projectName: string;
  projectHealthZone: HealthZone;
  accountId: string;
  accountName: string;
}

export interface AccountWithProjects {
  accountId: string;
  accountName: string;
  ownerAlias?: string;
  projects: DashboardProject[];
}

export interface AccountWithHealthZone extends AccountWithProjects {
  accountHealthZone: HealthZone;
}
